// The AppSumo activation token has to survive the signup round trip: the
// post-login redirect sends new users to onboarding, not back to the page
// they came from, so the token waits in localStorage and the App-level
// effect sends the user to the activation page once they are in. The
// signup redirectUrl param plays no part; storage is what brings them back.
const PENDING_TOKEN_KEY = "sg_appsumo_activation_token";
// The token the activation page has already run for. The App effect keeps
// sending the user to the page until it has actually run once, because the
// post-signup flow can navigate over the first redirect. After that, one
// failed activation must never trap the user on the page, and a blocked or
// rate-limited user must not reload in a loop.
const SEEN_KEY = "sg_appsumo_activation_seen";

export const ACTIVATE_PATH = "/appsumo/activate";

// Pages the App must never pull a buyer away from, even with a token
// pending: the whole signup and login round trip, the email confirmation
// page, and the pages that explain why the app refuses a request.
const BYPASS_PREFIXES = [
  "/login",
  "/signup",
  "/confirm-signup",
  "/auth/",
  "/logout",
  "/oauth/",
  "/blocked",
  "/rate-limited",
];

export const leavesActivationAlone = (pathname) =>
  pathname.startsWith(ACTIVATE_PATH) || BYPASS_PREFIXES.some((p) => pathname.startsWith(p));
// What activation learned about the deal, for the onboarding form to prefill
// the answers an AppSumo buyer has already given by buying.
const ONBOARDING_PREFILL_KEY = "sg_appsumo_onboarding_prefill";
export const APPSUMO_SOURCE = "AppSumo";

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // storage unavailable; the token in the URL still works for this visit
  }
};

export function storePendingAppsumoToken(token) {
  if (read(PENDING_TOKEN_KEY) !== token) {
    write(SEEN_KEY, null);
  }
  write(PENDING_TOKEN_KEY, token);
}

export function getPendingAppsumoToken() {
  return read(PENDING_TOKEN_KEY);
}

export function clearPendingAppsumoToken() {
  write(PENDING_TOKEN_KEY, null);
  write(SEEN_KEY, null);
}

// Whether the App should send the user to the activation page: a token is
// pending and the page has not run for it yet.
export function shouldRedirectToActivation() {
  const token = read(PENDING_TOKEN_KEY);
  return Boolean(token) && read(SEEN_KEY) !== token;
}

// The activation page calls this when it runs with a token. From here on
// the App stops redirecting for that token.
export function markActivationSeen(token) {
  write(SEEN_KEY, token);
}

export function storeAppsumoOnboardingPrefill({ accounts_allowed, plan_name }) {
  write(ONBOARDING_PREFILL_KEY, JSON.stringify({ accounts_allowed, plan_name }));
}

export function getAppsumoOnboardingPrefill() {
  try {
    return JSON.parse(read(ONBOARDING_PREFILL_KEY)) || null;
  } catch {
    return null;
  }
}

export function clearAppsumoOnboardingPrefill() {
  write(ONBOARDING_PREFILL_KEY, null);
}

// Hands the redirect back, for errors the buyer can fix and then retry.
export function allowAppsumoRedirect() {
  write(SEEN_KEY, null);
}
